import React, { useState } from 'react';
import { FaChevronLeft, FaChevronRight, FaIdCard, FaAddressBook, FaImage, FaEnvelopeOpenText, FaChartLine, FaCog } from 'react-icons/fa';
import Header1 from '../components/Header1';
import Footer from '../components/Footer';
import './Resources.css';

// 🎯 Dedicated, clean slider component for the Resources page
const ResourcesSlider = ({ images, sectionName }) => {
    const [currentIndex, setCurrentIndex] = useState(0);

    const handlePrev = () => {
        setCurrentIndex((prevIndex) => (prevIndex === 0 ? images.length - 1 : prevIndex - 1));
    };

    const handleNext = () => {
        setCurrentIndex((prevIndex) => (prevIndex === images.length - 1 ? 0 : prevIndex + 1));
    };

    return (
        <div className="resources-slider-wrapper">
            <div className="resources-slider-container">
                {/* 🎯 Slide Track */}
                <div 
                    className="resources-slider-track" 
                    style={{ transform: `translateX(-${currentIndex * 100}%)` }}
                >
                    {images.map((img, idx) => (
                        <div className="resources-slide" key={idx}>
                            <img src={img.src} alt={`${sectionName} - ${img.label}`} />
                            <span className="resources-slide-label">{img.label}</span>
                        </div>
                    ))}
                </div>

                {/* 🎯 Navigation Arrows */}
                {images.length > 1 && (
                    <>
                        <button className="resources-slider-arrow left" onClick={handlePrev} aria-label="Previous image">
                            <FaChevronLeft />
                        </button>
                        <button className="resources-slider-arrow right" onClick={handleNext} aria-label="Next image">
                            <FaChevronRight />
                        </button>
                    </>
                )}

                {/* 🎯 Pagination Dots */}
                {images.length > 1 && (
                    <div className="resources-slider-dots">
                        {images.map((_, idx) => (
                            <button
                                key={idx}
                                className={`resources-dot ${idx === currentIndex ? 'active' : ''}`}
                                onClick={() => setCurrentIndex(idx)}
                                aria-label={`Go to slide ${idx + 1}`}
                            />
                        ))}
                    </div>
                )}
            </div>
        </div>
    );
};

const Resources = () => {
    // 🎯 Grouping your asset images into sliding flows
    const resourceAssets = {
        cards: [
            { src: '/businesscards.png', label: 'My Cards' },
            { src: '/createcard.png', label: 'Create Card' },
            { src: '/editcard.png', label: 'Edit Card' }
        ],
        contacts: [
            { src: '/contacts.png', label: 'All Contacts' },
            { src: '/addcontact.png', label: 'Add Contact' },
            { src: '/editcontact.png', label: 'Edit Contact' }
        ],
        backgrounds: [
            { src: '/backgrounds.png', label: 'Virtual Backgrounds' }
        ],
        signatures: [
            { src: '/emailsignatures.png', label: 'Email Signatures' }
        ],
        analytics: [
            { src: '/analytics.png', label: 'Main Overview' },
            { src: '/analytics2.png', label: 'Trend Analysis' }
        ],
        settings: [
            // If you have a settings screenshot, you can put it here!
            { src: '/settings1.png', label: 'Account Settings' },
            { src: '/settings2.png', label: 'Subscription Management' }
        ]
    };

    return (
        <>
            <Header1 />
            <div className="resources-page">
                <section className="resources-hero">
                    <h1>Our Resources</h1>
                    <p>Discover the powerful features that make Digicard the ultimate tool for modern networking.</p>
                </section>

                <div className="resources-main">
                    {/* --- Cards Section --- */}
                    <section className="resources-feature">
                        <div className="resources-feature-text">
                            <div className="resources-feature-header">
                                <span className="resources-feature-icon"><FaIdCard /></span>
                                <h2>Digital Business Cards</h2>
                            </div>
                            <p>Our Digital Business Cards allow you to create, share, and manage your professional identity with ease. Customize your card to include all your essential information, and share it instantly with anyone, anywhere.</p>
                        </div>
                        <ResourcesSlider images={resourceAssets.cards} sectionName="Digital Business Cards" />
                    </section>

                    {/* --- Contacts Section --- */}
                    <section className="resources-feature resources-reverse">
                        <div className="resources-feature-text">
                            <div className="resources-feature-header">
                                <span className="resources-feature-icon"><FaAddressBook /></span>
                                <h2>Contacts</h2>
                            </div>
                            <p>Manage and organize your professional connections with our Contacts feature. Easily import, categorize, and search your contacts, ensuring that your network is always within reach.</p>
                        </div>
                        <ResourcesSlider images={resourceAssets.contacts} sectionName="Contacts" />
                    </section>

                    {/* --- Backgrounds Section --- */}
                    <section className="resources-feature">
                        <div className="resources-feature-text">
                            <div className="resources-feature-header">
                                <span className="resources-feature-icon"><FaImage /></span>
                                <h2>Virtual Backgrounds</h2>
                            </div>
                            <p>Enhance your online meetings with personalized Virtual Backgrounds. Keep your profile looking razor-sharp and consistent during video calls with customized digital backdrops.</p>
                        </div>
                        <ResourcesSlider images={resourceAssets.backgrounds} sectionName="Virtual Backgrounds" />
                    </section>

                    {/* --- Email Signatures Section --- */}
                    <section className="resources-feature resources-reverse">
                        <div className="resources-feature-text">
                            <div className="resources-feature-header">
                                <span className="resources-feature-icon"><FaEnvelopeOpenText /></span>
                                <h2>Email Signatures</h2>
                            </div>
                            <p>Design custom email signatures that represent your brand. Create clean, responsive, and clickable HTML signatures to link directly back to your digital business cards with every email sent.</p>
                        </div>
                        <ResourcesSlider images={resourceAssets.signatures} sectionName="Email Signatures" />
                    </section>

                    {/* --- Analytics Section --- */}
                    <section className="resources-feature">
                        <div className="resources-feature-text">
                            <div className="resources-feature-header">
                                <span className="resources-feature-icon"><FaChartLine /></span>
                                <h2>Analytics</h2>
                            </div>
                            <p>Track and analyze the performance of your networking efforts with our Analytics feature. Gain insights into how your digital cards are being used, understand your audience, and make data-driven decisions.</p>
                        </div>
                        <ResourcesSlider images={resourceAssets.analytics} sectionName="Analytics" />
                    </section>

                    {/* --- Settings Section --- */}
                    <section className="resources-feature resources-reverse">
                        <div className="resources-feature-text">
                            <div className="resources-feature-header">
                                <span className="resources-feature-icon"><FaCog /></span>
                                <h2>Settings</h2>
                            </div>
                            <p>Configure your account preferences, set up authentication triggers, and customize security controls to ensure your digital footprint remains safely locked down.</p>
                        </div>
                        <ResourcesSlider images={resourceAssets.settings} sectionName="Settings" />
                    </section>
                </div>
            </div>
            <Footer />
        </>
    );
};

export default Resources;